import { Injectable } from "@angular/core";
import { BehaviorSubject, filter } from "rxjs";
import { ILanguage } from "../../types/language";
import { AirlineService } from "./airline.service";
import { StorageService } from "./storageService.service";

@Injectable({ providedIn: 'root' })
export class LanguagesService {

    private _languages: ILanguage[] = [];

    constructor(
        private _airlineService: AirlineService,
        private _storage: StorageService
    ) {}

    //#region [main] Current Language

    private readonly _currentLanguageSubject = new BehaviorSubject<ILanguage>(null);
    readonly currentLanguageChange = this._currentLanguageSubject.asObservable();

    get currentLanguage(): ILanguage {
        return this._languages && this._languages.length > 0 ?
            this._tryToGetCurrentLanguage(this._languages) :
            null;
    }

    set currentLanguage(value: ILanguage) {
        this._storage.set.currentLanguage(value);
        this._currentLanguageSubject.next(value);
    }

    /**
     * Returns current language if any in the localStorage else returns the first in the language list.
     * @deprecated use currentLanguage property instead.
     */
    getCurrentLanguage(): Promise<ILanguage> {
        return Promise.resolve(this.currentLanguage);
    }

    /**
     * @deprecated
     */
    getCurrentLanguageSync(): ILanguage {
        return this.currentLanguage;
    }

    /**
     * Changes the current language
     */
    setCurrentLanguage(language: string | ILanguage): Promise<ILanguage> {
        if(typeof language == "string") language =  this._languages.find(lang => lang.language == language);
        if(language) {
            this.currentLanguage = language
            return Promise.resolve(language);
        }
        else Promise.reject(new Error('No language set for this airline.'));
    }

    private _tryToGetCurrentLanguage(languages: ILanguage[]) {
        const localLanguage = this._storage.get.currentLanguage();
        const subjectLanguage = this._currentLanguageSubject.value;
        const found = localLanguage ? languages.find(lang => lang.language == localLanguage.language) : null;
        const final = found ? found : languages ? languages[0] : null;
        if (final && (!localLanguage || found?.short != final.short)) {
            this._storage.set.currentLanguage(final);
            this._currentLanguageSubject.next(final);
        }
        else if (final?.short != subjectLanguage?.short) {
            this._currentLanguageSubject.next(final);
        }
        return final;
    }

    public observeLanguageChange() {
      return this._storage.observe.currentLanguage()
        .pipe(
          filter((changes) => changes.language != this._currentLanguageSubject.value.language)
        )
    }
    //#endregion

  public getAirlineLanguages(airlineId: string = ':airline_id:'): Promise<ILanguage[]> {
    return this._airlineService.getAirlineLanguages(airlineId)
      .then(res => {
        this._languages = res.languages.map(this._getFrontFormat);
        return this._languages;
      });
  }

  private _getFrontFormat(language: string): ILanguage {
    const [long, short] = language.split('_');
    return {
      long: long,
      short: short,
      language: language,
      label: `${long} (${short})`
    }
  }
}
